// ============================================================
// Chess Arena — Spotlight Resolver
// ============================================================
// Resolves spotlight target strings to screen-space rectangles
// for the tutorial system. Also manages tutorial move highlights
// and tutorial move filtering (one-step lookahead).
// ============================================================

import Phaser from 'phaser';
import { GridPos, PieceState, TutorialStep } from '../core/types';
import { Board } from '../core/board';
import { Piece } from '../core/piece';
import { getValidMoves } from '../core/moveValidator';
import { PieceManager } from './pieceManager';
import { TerrainSystem } from './terrainSystem';
import { TutorialManager } from './tutorialManager';

// -----------------------------------------------------------------
// Config passed at init
// -----------------------------------------------------------------

export interface SpotlightResolverConfig {
  scene: Phaser.Scene;
  board: Board;
  terrainSystem: TerrainSystem;
  pieceManager: PieceManager;
  tutorialManager: TutorialManager;
  cellSize: number;
  cellH: number;
  boardOriginX: number;
  boardOriginY: number;
  getAbilityButtonBounds: (abilityKey: string) => Phaser.Geom.Rectangle | null;
  getSpawnBarBounds: () => Phaser.Geom.Rectangle | null;
  rebuildAbilityButtons: () => void;
}

// -----------------------------------------------------------------
// SpotlightResolver
// -----------------------------------------------------------------

export class SpotlightResolver {
  private scene!: Phaser.Scene;
  private board!: Board;
  private terrainSystem!: TerrainSystem;
  private pieceManager!: PieceManager;
  private tutorialManager!: TutorialManager;
  private cellSize = 96;
  private cellH = 91;
  private boardOriginX = 0;
  private boardOriginY = 0;
  private getAbilityButtonBounds!: (abilityKey: string) => Phaser.Geom.Rectangle | null;
  private getSpawnBarBounds!: () => Phaser.Geom.Rectangle | null;
  private rebuildAbilityButtons!: () => void;

  // State
  tutorialHighlightStepId: string | null = null;
  tutorialMoveHighlights: Phaser.GameObjects.Graphics | null = null;

  // ------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------

  init(config: SpotlightResolverConfig): void {
    this.scene = config.scene;
    this.board = config.board;
    this.terrainSystem = config.terrainSystem;
    this.pieceManager = config.pieceManager;
    this.tutorialManager = config.tutorialManager;
    this.cellSize = config.cellSize;
    this.cellH = config.cellH;
    this.boardOriginX = config.boardOriginX;
    this.boardOriginY = config.boardOriginY;
    this.getAbilityButtonBounds = config.getAbilityButtonBounds;
    this.getSpawnBarBounds = config.getSpawnBarBounds;
    this.rebuildAbilityButtons = config.rebuildAbilityButtons;

    this.tutorialHighlightStepId = null;
    this.tutorialMoveHighlights = null;
  }

  // ------------------------------------------------------------------
  // resolveSpotlightTarget
  // ------------------------------------------------------------------

  /**
   * Convert a spotlight target string (e.g. "playerKnight", "enemyKing",
   * "spawnBar", "cell_2_3") into a screen-space rectangle.
   */
  resolveSpotlightTarget(target: string): Phaser.Geom.Rectangle | null {
    const CELL_SIZE = this.cellSize;
    const CELL_H = this.cellH;

    // Special dynamic targets
    if (target === 'lastMovedPiece' && this.pieceManager.lastMovedPieceId) {
      const lmp = this.pieceManager.getPieceById(this.pieceManager.lastMovedPieceId);
      if (lmp && lmp.pieceState.isAlive) {
        return new Phaser.Geom.Rectangle(
          lmp.x - CELL_SIZE * 0.5, lmp.y - CELL_SIZE * 0.5, CELL_SIZE, CELL_SIZE,
        );
      }
    }

    if (target === 'nearestEnemyPawn') {
      let nearest: Piece | null = null;
      for (const p of this.pieceManager.allPieces()) {
        if (p.pieceState.team === 'enemy' && p.pieceState.type === 'Pawn' && p.pieceState.isAlive) {
          if (!nearest || p.pieceState.pos.row > nearest.pieceState.pos.row) {
            nearest = p; // highest row = closest to player
          }
        }
      }
      if (nearest) {
        return new Phaser.Geom.Rectangle(
          nearest.x - CELL_SIZE * 0.5, nearest.y - CELL_SIZE * 0.5, CELL_SIZE, CELL_SIZE,
        );
      }
    }

    if (target === 'cooldownBar' && this.pieceManager.lastMovedPieceId) {
      // Same as lastMovedPiece — spotlight the piece showing its cooldown
      const lmp = this.pieceManager.getPieceById(this.pieceManager.lastMovedPieceId);
      if (lmp && lmp.pieceState.isAlive) {
        return new Phaser.Geom.Rectangle(
          lmp.x - CELL_SIZE * 0.5, lmp.y - CELL_SIZE * 0.5, CELL_SIZE, CELL_SIZE,
        );
      }
    }

    // cooldownPiece: first player piece currently on cooldown
    if (target === 'cooldownPiece') {
      for (const p of this.pieceManager.allPieces()) {
        if (p.pieceState.team === 'player' && p.pieceState.isAlive && p.pieceState.cooldownRemaining > 0) {
          return new Phaser.Geom.Rectangle(
            p.x - CELL_SIZE * 0.5, p.y - CELL_SIZE * 0.5, CELL_SIZE, CELL_SIZE,
          );
        }
      }
    }

    // Try to find a piece matching the target
    // Target naming: "playerKing", "playerKnight", "enemyKing", "enemyPawn", etc.
    for (const piece of this.pieceManager.allPieces()) {
      if (!piece.pieceState.isAlive) continue;

      const teamPrefix = piece.pieceState.team === 'player' ? 'player' : 'enemy';
      const matchKey = `${teamPrefix}${piece.pieceState.type}`;

      if (target.toLowerCase() === matchKey.toLowerCase()) {
        return new Phaser.Geom.Rectangle(
          piece.x - CELL_SIZE * 0.4,
          piece.y - CELL_SIZE * 0.4,
          CELL_SIZE * 0.8,
          CELL_SIZE * 0.8,
        );
      }
    }

    // Grid position target: "cell_2_3" → col 2, row 3
    const cellMatch = target.match(/^cell_(\d+)_(\d+)$/);
    if (cellMatch) {
      const col = parseInt(cellMatch[1], 10);
      const row = parseInt(cellMatch[2], 10);
      const pixel = Board.gridToPixel({ col, row }, CELL_SIZE, this.boardOriginX, this.boardOriginY, CELL_H);
      return new Phaser.Geom.Rectangle(
        pixel.x - CELL_SIZE / 2,
        pixel.y - CELL_H / 2,
        CELL_SIZE,
        CELL_H,
      );
    }

    // Terrain targets: rockTile / rock_tile / bushTile / bush_tile
    const terrainTargetMatch = target.match(/^(rock|bush)[_]?tile$/i);
    if (terrainTargetMatch) {
      const wantedType = terrainTargetMatch[1].toLowerCase() as 'rock' | 'bush';
      const allTerrain = this.terrainSystem.getAllTerrain();
      const match = allTerrain.find(t => t.type === wantedType);
      if (match) {
        const pixel = Board.gridToPixel(match.pos, CELL_SIZE, this.boardOriginX, this.boardOriginY, CELL_H);
        return new Phaser.Geom.Rectangle(
          pixel.x - CELL_SIZE / 2,
          pixel.y - CELL_H / 2,
          CELL_SIZE,
          CELL_H,
        );
      }
    }

    // Piece in terrain: "playerPieceInBush" / "playerPieceInRock"
    const pieceInTerrainMatch = target.match(/^playerPieceIn(Bush|Rock)$/i);
    if (pieceInTerrainMatch) {
      const terrainType = pieceInTerrainMatch[1].toLowerCase() as 'rock' | 'bush';
      for (const piece of this.pieceManager.allPieces()) {
        if (!piece.pieceState.isAlive || piece.pieceState.team !== 'player') continue;
        const terrain = this.terrainSystem.getTerrainAt(piece.pieceState.pos);
        if (terrain && terrain.type === terrainType) {
          return new Phaser.Geom.Rectangle(
            piece.x - CELL_SIZE * 0.5, piece.y - CELL_SIZE * 0.5, CELL_SIZE, CELL_SIZE,
          );
        }
      }
    }

    // HUD elements
    if (target === 'spawnBar') {
      const bounds = this.getSpawnBarBounds();
      if (bounds) return bounds;
      // Fallback: estimate spawn bar area
      return new Phaser.Geom.Rectangle(
        16, this.scene.cameras.main.height - 200 + 8,
        this.scene.cameras.main.width - 32, 120,
      );
    }

    // Ability button targets: "abilityButton_bomb", "abilityButton_freeze", etc.
    const abilityBtnMatch = target.match(/^abilityButton_(.+)$/);
    if (abilityBtnMatch) {
      const abilityId = abilityBtnMatch[1];
      const btnBounds = this.getAbilityButtonBounds(abilityId);
      if (btnBounds) return btnBounds;

      // Ability button doesn't exist yet — trigger a HUD rebuild and try again
      this.rebuildAbilityButtons();
      const retryBounds = this.getAbilityButtonBounds(abilityId);
      if (retryBounds) return retryBounds;
    }

    return null;
  }

  // ------------------------------------------------------------------
  // updateTutorialMoveHighlights
  // ------------------------------------------------------------------

  /**
   * Show persistent valid-move highlights during action-gated tutorial steps
   * so the player can see where the highlighted piece can move.
   */
  updateTutorialMoveHighlights(tutorialActive: boolean | null): void {
    if (!tutorialActive) {
      this.clearTutorialMoveHighlights();
      return;
    }

    const step = this.tutorialManager.getCurrentStep();
    const stepId = step?.stepId ?? null;

    // Only show highlights for steps that require the player to move or queue
    if (!step || !step.requiredAction
      || (step.requiredAction !== 'piece_moved_complete' && step.requiredAction !== 'premove_queued')) {
      this.clearTutorialMoveHighlights();
      return;
    }

    // Already showing highlights for this step
    if (this.tutorialHighlightStepId === stepId) return;

    this.clearTutorialMoveHighlights();
    this.tutorialHighlightStepId = stepId;

    // Find the target piece based on spotlight target
    const target = step.spotlightTarget;
    if (!target) return;

    let targetPiece: Piece | null = null;
    if (target === 'cooldownPiece') {
      for (const p of this.pieceManager.allPieces()) {
        if (p.pieceState.team === 'player' && p.pieceState.isAlive && p.pieceState.cooldownRemaining > 0) {
          targetPiece = p;
          break;
        }
      }
    } else if (target === 'lastMovedPiece' && this.pieceManager.lastMovedPieceId) {
      targetPiece = this.pieceManager.getPieceById(this.pieceManager.lastMovedPieceId) ?? null;
    } else {
      // Match by type: "playerKnight" etc.
      for (const p of this.pieceManager.allPieces()) {
        if (!p.pieceState.isAlive) continue;
        const prefix = p.pieceState.team === 'player' ? 'player' : 'enemy';
        const key = `${prefix}${p.pieceState.type}`;
        if (target.toLowerCase() === key.toLowerCase()) {
          targetPiece = p;
          break;
        }
      }
    }

    if (!targetPiece) return;

    // Draw persistent highlights for this piece's valid moves
    const allStates = this.pieceManager.getAllPieceStates();
    let moves = getValidMoves(targetPiece.pieceState, this.board, allStates);

    // Tutorial: filter moves so only reachable-then-capturable squares are shown
    moves = this.filterTutorialMoves(targetPiece, moves, allStates);
    if (moves.length === 0) return;

    // Use gold color for premove highlights, green for normal moves
    const isPremoveStep = step.requiredAction === 'premove_queued';
    const color = isPremoveStep ? 0xffd700 : 0x4ade80;

    const CELL_SIZE = this.cellSize;
    const CELL_H = this.cellH;

    this.tutorialMoveHighlights = this.scene.add.graphics();
    this.tutorialMoveHighlights.setDepth(201);

    for (const move of moves) {
      const pixel = Board.gridToPixel(move, CELL_SIZE, this.boardOriginX, this.boardOriginY, CELL_H);
      // Tinted cell background
      this.tutorialMoveHighlights.fillStyle(color, 0.2);
      this.tutorialMoveHighlights.fillRect(
        pixel.x - CELL_SIZE / 2, pixel.y - CELL_H / 2,
        CELL_SIZE, CELL_H,
      );
      // Border around valid cell
      this.tutorialMoveHighlights.lineStyle(2, color, 0.6);
      this.tutorialMoveHighlights.strokeRect(
        pixel.x - CELL_SIZE / 2, pixel.y - CELL_H / 2,
        CELL_SIZE, CELL_H,
      );
      // Center dot
      this.tutorialMoveHighlights.fillStyle(color, 0.9);
      this.tutorialMoveHighlights.fillCircle(pixel.x, pixel.y, CELL_SIZE * 0.12);
      // Outer glow ring
      this.tutorialMoveHighlights.fillStyle(color, 0.3);
      this.tutorialMoveHighlights.fillCircle(pixel.x, pixel.y, CELL_SIZE * 0.22);
    }

    // Pulse animation so the highlights draw attention
    this.scene.tweens.add({
      targets: this.tutorialMoveHighlights,
      alpha: { from: 1, to: 0.5 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  // ------------------------------------------------------------------
  // clearTutorialMoveHighlights
  // ------------------------------------------------------------------

  clearTutorialMoveHighlights(): void {
    if (this.tutorialMoveHighlights) {
      this.scene.tweens.killTweensOf(this.tutorialMoveHighlights);
      this.tutorialMoveHighlights.destroy();
      this.tutorialMoveHighlights = null;
    }
    this.tutorialHighlightStepId = null;
  }

  // ------------------------------------------------------------------
  // filterTutorialMoves
  // ------------------------------------------------------------------

  /**
   * During tutorial, filter valid moves so only squares that lead to
   * capturing the next tutorial target (one-step lookahead) are shown.
   */
  filterTutorialMoves(piece: Piece, moves: GridPos[], allStates: PieceState[]): GridPos[] {
    if (!this.tutorialManager || !this.tutorialManager.isActive()) return moves;

    // Look past info-only steps (like "cooldown") to find the next action step
    const nextStep = this.tutorialManager.getNextActionStep();
    if (!nextStep || nextStep.requiredAction !== 'piece_captured') return moves;

    // Find enemy targets referenced in the next step's spotlight
    const nextTargets = (nextStep.spotlightTarget ?? '').split(',').map((t) => t.trim().toLowerCase());
    const enemyTargetPositions: GridPos[] = [];

    for (const ps of allStates) {
      if (!ps.isAlive || ps.team === 'player') continue;
      const key = `enemy${ps.type}`.toLowerCase();
      // Check if this enemy matches any of the next step's spotlight targets
      // Also match dynamic targets like "nearestenemypawn"
      const matches = nextTargets.some((t) =>
        t === key || (t === 'nearestenemypawn' && ps.type === 'Pawn'),
      );
      if (matches) {
        enemyTargetPositions.push(ps.pos);
      }
    }

    if (enemyTargetPositions.length === 0) return moves;

    // For each candidate move, simulate the piece at that position and check
    // if any of its new valid moves can reach an enemy target
    return moves.filter((candidate) => {
      const simPiece: PieceState = { ...piece.pieceState, pos: candidate };
      const futureMoves = getValidMoves(simPiece, this.board, allStates);
      return futureMoves.some((fm) =>
        enemyTargetPositions.some((ep) => fm.col === ep.col && fm.row === ep.row),
      );
    });
  }

  // ------------------------------------------------------------------
  // Destroy
  // ------------------------------------------------------------------

  destroy(): void {
    this.clearTutorialMoveHighlights();
  }
}
